/**
 * Legacy admin "Operator Console" — the page rendered at /admin?tab=<slug>.
 *
 * Visual chrome lives in V2AdminNavbar now: the slim sticky nav strip at
 * the top is the same one the V2 admin pages use, which keeps both halves
 * of the admin app visually coherent. This component only owns:
 *
 *   - the controlled <Tabs> + <TabsContent> panels that render the actual
 *     section bodies (Analytics, Tenants, Properties, Payments, etc.)
 *   - the URL ?tab= sync so deep-links land on the right panel
 *
 * The TabsList (visible tab triggers) used to render here as a row of
 * large pill buttons, but it duplicated the V2AdminNavbar's role. Now
 * V2AdminNavbar's "legacy" entries (kind: 'legacy') write the ?tab=
 * param via plain <Link> navigation, and this component reacts to that
 * via useSearchParams. There is no visible TabsList — Radix's <Tabs>
 * works fine without one when the value is fully URL-driven.
 */
import { useState, useEffect } from "react";
import {
  Link,
  useSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminV2Enabled } from "@/lib/featureFlags";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { V2AdminNavbar } from "@/components/admin/v2/V2AdminNavbar";
import { AdminProperties } from "@/components/admin/AdminProperties";
import AdminReminders from "@/components/admin/AdminReminders";
import PaymentsHub from "@/components/admin/payments/PaymentsHub";
import { AdminTenants } from "@/components/admin/AdminTenants";
import { AdminDocuments } from "@/components/admin/AdminDocuments";
import { SupportInbox } from "@/components/admin/support/SupportInbox";
import { AdminAnalytics } from "@/components/admin/AdminAnalytics";
import { AdminOwnerPortfolio } from "@/components/admin/AdminOwnerPortfolio";
import { AdminContractTemplates } from "@/components/admin/AdminContractTemplates";
import AdminSettings from "@/components/admin/AdminSettings";
import { AdminAuditLogs } from "@/components/admin/AdminAuditLogs";
import OwnerOnboardingAdmin from "@/components/admin/OwnerOnboardingAdmin";
import { OwnerContractTemplates } from "@/components/admin/OwnerContractTemplates";
import { AdminInvoiceTemplates } from "@/components/admin/AdminInvoiceTemplates";

/** Legacy ?tab= values owners may open (V2 property URLs are separate). */
const OWNER_CONSOLE_TABS = new Set(["analytics", "properties", "settings"]);

/**
 * Legacy ?tab= values operator-portal accounts may open (COL-352). Everything
 * else (payments, documents, owner-onboarding, reminders, the legacy
 * properties tab) is CoLiviq-internal; operators use the V2 property routes.
 * Data inside the allowed tabs is scoped by operator RLS — this list is the
 * URL-level gate on top of the hidden nav.
 */
const OPERATOR_CONSOLE_TABS = new Set([
  "analytics",
  "tenants",
  "issues",
  "settings",
  "contract-templates",
  // Own-org activity only — the boundary is the audit_logs RLS operator arm.
  "audit",
]);

/**
 * Retired admin tabs (COL-462). The screen is hidden, not deleted: the
 * component, its hooks and its tests all stay, so flipping this back to
 * `false` restores the tab exactly as it was.
 *
 * Payments went because Reconciliation covers direct transfers, direct debits
 * and card payments are marked on the tenant/owner/operator profiles, and the
 * monthly management-invoice run is now automated rather than a button here.
 */
const PAYMENTS_TAB_RETIRED: boolean = true;

const RETIRED_CONSOLE_TABS = new Set(PAYMENTS_TAB_RETIRED ? ["payments"] : []);

/** Where a bookmark or stale link to a retired tab lands instead. */
const RETIRED_TAB_FALLBACK = "/admin/reconciliation";

const OperatorConsole = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading: authLoading,
    isOwnerPortalOnly,
    isOperatorPortal,
  } = useAuth();
  const tabFromUrl = searchParams.get("tab") || "analytics";
  const openBookingIdFromState = (
    location.state as { openBookingId?: number } | null
  )?.openBookingId;
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        if (value === "tenants") {
          next.delete("bookingId");
        }
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (authLoading || !isOwnerPortalOnly) return;
    if (tabFromUrl === "my-property" || !OWNER_CONSOLE_TABS.has(tabFromUrl)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "analytics");
          return next;
        },
        { replace: true }
      );
    }
  }, [authLoading, isOwnerPortalOnly, tabFromUrl, setSearchParams]);

  // Same URL-level gate for operator-portal accounts: typing ?tab=payments
  // must land on analytics, not on a CoLiviq-internal panel.
  useEffect(() => {
    if (authLoading || !isOperatorPortal) return;
    if (!OPERATOR_CONSOLE_TABS.has(tabFromUrl)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "analytics");
          return next;
        },
        { replace: true }
      );
    }
  }, [authLoading, isOperatorPortal, tabFromUrl, setSearchParams]);

  // A retired tab keeps answering its old URL, so bookmarks and links that
  // predate the retirement land somewhere real instead of an empty panel.
  // Owner- and operator-portal accounts are handled by the gates above, which
  // send them to their own analytics rather than a staff screen.
  useEffect(() => {
    if (authLoading || isOwnerPortalOnly || isOperatorPortal) return;
    if (!RETIRED_CONSOLE_TABS.has(tabFromUrl)) return;
    navigate(RETIRED_TAB_FALLBACK, { replace: true });
  }, [authLoading, isOwnerPortalOnly, isOperatorPortal, tabFromUrl, navigate]);

  return (
    <div
      className={
        isOwnerPortalOnly
          ? "min-h-screen bg-background"
          : "min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-muted/50 via-background to-background"
      }
    >
      <V2AdminNavbar />
      <div
        className={`mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 ${
          // Owners get the same 1920px cap as every AdminV2Layout page
          // (Dokumente, Payouts, …); staff keep the console's own 2xl cap.
          isOwnerPortalOnly ? "w-full max-w-[1920px]" : "max-w-screen-2xl"
        }`}
      >
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="space-y-4 sm:space-y-6"
        >
          {/* No <TabsList> on purpose — V2AdminNavbar is the visible tab
              chooser. TabsContent below is still controlled by `value`
              on the parent <Tabs>, which is driven by ?tab= via the
              useSearchParams effect above. */}

          <TabsContent value="analytics">
            {isOwnerPortalOnly ? <AdminOwnerPortfolio /> : <AdminAnalytics />}
          </TabsContent>

          <TabsContent value="tenants">
            <AdminTenants
              openBookingIdFromUrl={
                openBookingIdFromState ??
                searchParams.get("bookingId") ??
                undefined
              }
              onClearBookingIdFromUrl={() => {
                navigate("/admin?tab=tenants", { replace: true, state: {} });
              }}
            />
          </TabsContent>

          {/* CoLiviq-internal panels are not even mounted for operator-portal
              accounts: the redirect effect above runs after first render, and
              a panel must never exist for the frame in between. */}
          {!isOperatorPortal && (
            <TabsContent value="properties">
              {isAdminV2Enabled() && (
                <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 px-4 py-3 text-sm flex items-center justify-between gap-3">
                  <div>
                    <span className="font-medium">V2 admin is available.</span>{" "}
                    <span className="text-muted-foreground">
                      Try the redesigned property workflows — lessors, mass room
                      create, property/room/tenant detail screens.
                    </span>
                  </div>
                  <Link
                    to="/admin/properties"
                    className="shrink-0 underline hover:no-underline font-medium"
                  >
                    Open V2 →
                  </Link>
                </div>
              )}
              <AdminProperties />
            </TabsContent>
          )}

          {!isOperatorPortal && !PAYMENTS_TAB_RETIRED && (
            <TabsContent value="payments">
              <PaymentsHub />
            </TabsContent>
          )}

          {!isOperatorPortal && (
            <TabsContent value="reminders">
              <AdminReminders />
            </TabsContent>
          )}

          {!isOperatorPortal && (
            <TabsContent value="documents">
              <AdminDocuments />
            </TabsContent>
          )}

          <TabsContent value="contract-templates">
            <div className="space-y-6">
              <AdminContractTemplates />
              <OwnerContractTemplates />
              <AdminInvoiceTemplates />
            </div>
          </TabsContent>

          <TabsContent value="issues">
            <SupportInbox />
          </TabsContent>

          {/* Also the own-org activity trail for operators (rollout notes:
              "only show the operator's own activity — since sign-up"). RLS
              scopes the rows; staff see what their role's policies grant. */}
          <TabsContent value="audit">
            <AdminAuditLogs />
          </TabsContent>

          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>

          {!isOperatorPortal && (
            <TabsContent value="owner-onboarding">
              <OwnerOnboardingAdmin />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default OperatorConsole;
